import { PageName } from '@/router/routes/constants';
import dictionaryService from '@/core/translation/dictionary.service';
import authService from '@/core/auth/auth.service';
import { permissionForRoute } from '@/router/routes/client-routes/my-account.routes';
import permissionService from '@/project/auth/permission.service';
import { router } from '@/router/index';
import serverContextService from '@/core/serverContext.service';
import { Feature } from '@/types/apiServerContract';
import myAccountStore from '@/project/app/client-pages/my-account/my-account.store';
import Api from '@/project/http/Api.service';
import { reactive } from 'vue';

export interface ClientMenuItem {
    page?: string;
    url?: string;
    name: string;
    icon?: string;
    iconFill?: boolean; // as default on <CIcon> would be true, but we want a few icons to be false, usage would be something like this <CIcon name="..." fill="item.iconFill === undefined || item.iconFill === true"/>
    children?: ClientMenuItem[],
    isPrivate?: boolean;
    action?: () => void;
}

interface INavigationStoreState {
    myAccountNavigationGeneral: ClientMenuItem[];
    myAccountNavigationMisc: ClientMenuItem[];
    myAccountNavigationActions: ClientMenuItem[];
}
class NavigationStore {
    private state: INavigationStoreState = reactive({
        myAccountNavigationGeneral: ([
            {
                name: dictionaryService.get('MyAccount.Profile'),
                icon: 'profile',
                page: PageName.MY_ACCOUNT_PROFILE
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.OrderHistory'),
                icon: 'order-history',
                page: PageName.MY_ACCOUNT_ORDER_HISTORY
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.ProductBacklog'),
                icon: 'stacked',
                page: PageName.MY_ACCOUNT_PRODUCT_BACKLOG
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.Quotes'),
                icon: 'document',
                page: PageName.MY_ACCOUNT_QUOTES
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.BlanketOrders'),
                icon: 'blanket-order',
                page: PageName.MY_ACCOUNT_BLANKET_ORDERS
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.Bids'),
                icon: 'bid',
                page: PageName.MY_ACCOUNT_BIDS
            },
            serverContextService.isFeatureEnabled(Feature.RmaV2) && {
                name: dictionaryService.get('MyAccount.Navigation.RMAReturns'),
                icon: 'rma',
                page: PageName.MY_ACCOUNT_RMA_RETURN
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.Ledger'),
                icon: 'ledger',
                page: PageName.MY_ACCOUNT_LEDGER
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.AssortmentLists'),
                icon: 'assortment-list',
                page: PageName.MY_ACCOUNT_PRODUCT_LISTS
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.ProductGuideConfiguration'),
                icon: 'productguide',
                page: PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.RackConfigurations'),
                icon: 'rack',
                page: PageName.MY_ACCOUNT_RACK_CONFIGURATIONS
            },
            serverContextService.isFeatureEnabled(Feature.PartnerPrograms) && {
                name: dictionaryService.get('MyAccount.Navigation.PartnerPrograms'),
                icon: 'partner-programs',
                page: PageName.MY_ACCOUNT_PARTNER_PROGRAMS
            }
        ].filter(<T>(n?: T): n is T => Boolean(n))) as ClientMenuItem[],
        myAccountNavigationMisc: [
            {
                name: dictionaryService.get('MyAccount.Navigation.Favourites'),
                icon: 'favorite-outline',
                page: PageName.MY_ACCOUNT_FAVOURITES
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.AccountSettings'),
                icon: 'account-setting',
                page: PageName.MY_ACCOUNT_ACCOUNT_SETTINGS
            },
            serverContextService.isFeatureEnabled(Feature.MyAccountServices) && {
                name: dictionaryService.get('MyAccount.Navigation.Services'),
                icon: 'puzzle',
                page: PageName.MY_ACCOUNT_SERVICES
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.AddressBook'),
                icon: 'address-book',
                page: PageName.MY_ACCOUNT_ADDRESS_BOOK
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.UserAdministration'),
                icon: 'profiles',
                page: PageName.MY_ACCOUNT_USER_ADMINISTRATION
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.Impersonation'),
                icon: 'impersonate',
                page: PageName.MY_ACCOUNT_IMPERSONATION
            },
            {
                name: dictionaryService.get('MyAccount.Navigation.NextDeliveryAdministration'),
                icon: 'truck',
                page: PageName.MY_ACCOUNT_NEXT_DELIVERY
            }
        ].filter(<T>(n?: T): n is T => Boolean(n)) as ClientMenuItem[],
        myAccountNavigationActions: [
            {
                name: dictionaryService.get('MyAccount.Navigation.SignOut'),
                icon: 'login',
                action: () => {
                    authService.logOff();
                    Api.authentication.logOff();
                    router.push('/').catch(() => { });
                },
                isPrivate: true
            }
        ]
    });

    public get clientNavigationItems(): ClientMenuItem {
        return {
            name: dictionaryService.get('MyAccount.FrontPage.Title'),
            icon: 'profile',
            page: PageName.MY_ACCOUNT,
            children: [...this.myAccountNavigationGeneral, ...this.myAccountNavigationMisc, ...this.myAccountNavigationActions],
            isPrivate: true
        };
    }

    showNavElement(item: ClientMenuItem): boolean {
        if (item.isPrivate && !authService.isLoggedIn()) return false;
        if (item.page) {
            const permission = permissionForRoute(item.page);
            if (permission) {
                return permissionService.can(permission);
            }
        }
        return true;
    }

    public get myAccountNavigationGeneral() {
        return this.state.myAccountNavigationGeneral.filter(navItem => {
            if (navItem.page === PageName.MY_ACCOUNT_PRODUCT_GUIDE_CONFIGURATION) {
                return myAccountStore.hasProductGuideConfigurations;
            }
            if (navItem.page === PageName.MY_ACCOUNT_RACK_CONFIGURATIONS) {
                return myAccountStore.showRackConfigurationsMenuItem;
            }
            if (navItem.page === PageName.MY_ACCOUNT_PRODUCT_LISTS) {
                return myAccountStore.showAssortmentListsMenuItem;
            }
            if (navItem.page === PageName.MY_ACCOUNT_PARTNER_PROGRAMS) {
                if (!serverContextService.isFeatureEnabled(Feature.PartnerPrograms)) {
                    return false;
                }
                // Always show for Silver, Gold and Platinum
                if (
                    permissionService.isSalesCategorySilver() ||
                    permissionService.isSalesCategoryGold() ||
                    permissionService.isSalesCategoryPlatinum()
                ) {
                    return true;
                }
                // Bronze: show if they have any partner programs
                if (permissionService.isSalesCategoryBronze()) {
                    return (myAccountStore.partnerProgramsActive?.length ?? 0) > 0; // only show menu item if there are partner programs to show
                }
                // Leads: hide
                return false;
            }
            return true;
        });
    }

    public get myAccountNavigationMisc() {
        return this.state.myAccountNavigationMisc.filter(navItem => {
            if (navItem.page === PageName.MY_ACCOUNT_SERVICES) {
                return serverContextService.isFeatureEnabled(Feature.MyAccountServices);
            }
            if (navItem.page === PageName.MY_ACCOUNT_NEXT_DELIVERY) {
                return myAccountStore.showNextDeliveryMenuItem;
            }
            return true;
        });
    }

    public get myAccountNavigationActions() {
        return this.state.myAccountNavigationActions;
    }
}
export default new NavigationStore();
